import { BoardStroke } from "../../shared/boards";

// the retouch tools work on what is already painted on their layer instead of adding lines: smudge drags the
// paint along, blend drags it and mixes the colour in, blur softens it, lighten and darken push it up or down.
// a stroke is a row of dabs along its path. every dab only reads the canvas as it is right then, so painting a
// stroke bit by bit while the pen moves and painting it whole later give the same picture, which is what lets
// everyone replay the same stroke and see the same thing

export type RetouchKind = "smudge" | "blend" | "blur" | "lighten" | "darken";

export interface RetouchLook {
  // how soft the edge of the dab is, 0 to 1
  soft: number;
  // how much pressure changes the strength, 0 to 1
  press: number;
}

// where a stroke got to. smudge and blend carry a patch of paint along with them
export interface RetouchState {
  next: number;
  last: [number, number] | null;
  lastPressure: number;
  owed: number;
  carry: HTMLCanvasElement | null;
}

export function freshRetouch(): RetouchState {
  return { next: 0, last: null, lastPressure: 1, owed: 0, carry: null };
}

const MAX_RADIUS = 220;
const MAX_DABS = 6000;
const scratch = new Map<string, HTMLCanvasElement>();
const masks = new Map<string, HTMLCanvasElement>();

function sheet(name: string, side: number): HTMLCanvasElement {
  let canvas = scratch.get(name);
  if (!canvas) {
    canvas = document.createElement("canvas");
    scratch.set(name, canvas);
  }
  if (canvas.width !== side || canvas.height !== side) {
    canvas.width = side;
    canvas.height = side;
  }
  return canvas;
}

// a white disc that fades out at the edge, as soft as asked
function maskFor(side: number, soft: number): HTMLCanvasElement {
  const key = `${side}|${Math.round(soft * 20)}`;
  let mask = masks.get(key);
  if (mask) return mask;
  if (masks.size > 40) masks.clear();
  mask = document.createElement("canvas");
  mask.width = side;
  mask.height = side;
  const ctx = mask.getContext("2d");
  if (ctx) {
    const half = side / 2;
    const gradient = ctx.createRadialGradient(half, half, half * Math.max(0, 0.92 - soft * 0.92), half, half, half);
    gradient.addColorStop(0, "rgba(255,255,255,1)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, side, side);
  }
  masks.set(key, mask);
  return mask;
}

// a copy of the canvas around a point, faded at the edge
function lift(ctx: CanvasRenderingContext2D, into: HTMLCanvasElement, x: number, y: number, radius: number, soft: number): void {
  const paint = into.getContext("2d");
  if (!paint) return;
  const side = into.width;
  paint.setTransform(1, 0, 0, 1, 0, 0);
  paint.globalAlpha = 1;
  paint.globalCompositeOperation = "copy";
  paint.drawImage(ctx.canvas, Math.round(x - radius), Math.round(y - radius), side, side, 0, 0, side, side);
  paint.globalCompositeOperation = "destination-in";
  paint.drawImage(maskFor(side, soft), 0, 0);
  paint.globalCompositeOperation = "source-over";
}

// lay a patch over the canvas: what was under the soft disc gives way to the patch by `amount`
function lay(ctx: CanvasRenderingContext2D, patch: HTMLCanvasElement, x: number, y: number, radius: number, soft: number, amount: number): void {
  const side = patch.width;
  const at = [Math.round(x - radius), Math.round(y - radius)];
  ctx.globalAlpha = amount;
  ctx.globalCompositeOperation = "destination-out";
  ctx.drawImage(maskFor(side, soft), at[0], at[1]);
  ctx.globalCompositeOperation = "source-over";
  ctx.drawImage(patch, at[0], at[1]);
  ctx.globalAlpha = 1;
}

function dab(ctx: CanvasRenderingContext2D, kind: RetouchKind, state: RetouchState, x: number, y: number, radius: number, strength: number, color: string, look: RetouchLook): void {
  const side = Math.max(4, Math.ceil(radius * 2));
  if (kind === "lighten" || kind === "darken") {
    // brightens or dims what is there and never paints onto empty paper
    const gradient = ctx.createRadialGradient(x, y, radius * Math.max(0, 0.92 - look.soft * 0.92), x, y, radius);
    const tone = kind === "lighten" ? "255,255,255" : "0,0,0";
    gradient.addColorStop(0, `rgba(${tone},${0.16 * strength})`);
    gradient.addColorStop(1, `rgba(${tone},0)`);
    ctx.globalCompositeOperation = "source-atop";
    ctx.fillStyle = gradient;
    ctx.fillRect(x - radius, y - radius, side, side);
    ctx.globalCompositeOperation = "source-over";
    return;
  }
  if (kind === "blur") {
    const patch = sheet("patch", side);
    lift(ctx, patch, x, y, radius, 0);
    // squash it small and stretch it back, which is a blur that works in every browser
    const factor = Math.max(2, Math.min(10, Math.round(radius / 5)));
    const tiny = sheet("tiny", Math.max(2, Math.ceil(side / factor)));
    const little = tiny.getContext("2d");
    const blurred = sheet("blurred", side);
    const back = blurred.getContext("2d");
    if (!little || !back) return;
    little.setTransform(1, 0, 0, 1, 0, 0);
    little.globalCompositeOperation = "copy";
    little.imageSmoothingEnabled = true;
    little.imageSmoothingQuality = "high";
    little.drawImage(patch, 0, 0, tiny.width, tiny.height);
    back.setTransform(1, 0, 0, 1, 0, 0);
    back.globalCompositeOperation = "copy";
    back.imageSmoothingEnabled = true;
    back.imageSmoothingQuality = "high";
    back.drawImage(tiny, 0, 0, side, side);
    back.globalCompositeOperation = "destination-in";
    back.drawImage(maskFor(side, look.soft), 0, 0);
    back.globalCompositeOperation = "source-over";
    lay(ctx, blurred, x, y, radius, look.soft, 0.25 + 0.6 * strength);
    return;
  }
  // smudge and blend: the first dab only picks paint up, every one after sets some down and picks up again
  if (!state.carry || state.carry.width !== side) {
    state.carry = document.createElement("canvas");
    state.carry.width = side;
    state.carry.height = side;
    lift(ctx, state.carry, x, y, radius, look.soft);
    return;
  }
  const carry = state.carry;
  const hold = carry.getContext("2d");
  if (!hold) return;
  if (kind === "blend") {
    // a little of the brush colour goes into what is carried, so it mixes as it goes
    const half = side / 2;
    const tint = hold.createRadialGradient(half, half, 0, half, half, half);
    tint.addColorStop(0, color);
    tint.addColorStop(1, "rgba(0,0,0,0)");
    hold.globalAlpha = 0.05 + 0.1 * strength;
    hold.fillStyle = tint;
    hold.fillRect(0, 0, side, side);
    hold.globalAlpha = 1;
  }
  lay(ctx, carry, x, y, radius, look.soft, 0.3 + 0.65 * strength);
  const fresh = sheet("fresh", side);
  lift(ctx, fresh, x, y, radius, look.soft);
  // what is carried stays mostly as it was, so a hard push drags a long way and a light one fades quickly
  const keep = 0.8 + 0.19 * strength;
  const mix = fresh.getContext("2d");
  if (!mix) return;
  mix.globalAlpha = keep;
  mix.drawImage(carry, 0, 0);
  mix.globalAlpha = 1;
  hold.globalCompositeOperation = "copy";
  hold.drawImage(fresh, 0, 0);
  hold.globalCompositeOperation = "source-over";
}

// carries on a stroke from where its state got to. the context is the layer's, in board units, the way the
// view draws it. points not yet seen are dabbed, the rest is left alone
export function retouchStroke(ctx: CanvasRenderingContext2D, stroke: BoardStroke, kind: RetouchKind, look: RetouchLook, alpha: number, state: RetouchState): void {
  const pts = stroke.pts;
  const total = Math.floor(pts.length / 3);
  if (state.next >= total) return;
  const m = ctx.getTransform();
  const scale = Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) || 1;
  const radius = Math.min(MAX_RADIUS, Math.max(1.5, (stroke.size / 2) * scale));
  const step = Math.max(1.2, radius * (kind === "smudge" || kind === "blend" ? 0.18 : 0.3));
  const strengthAt = (pressure: number) => Math.min(1, Math.max(0.02, alpha * (1 - look.press + look.press * pressure)));
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  let made = 0;
  for (let at = state.next; at < total; at++) {
    const x = m.a * pts[at * 3] + m.c * pts[at * 3 + 1] + m.e;
    const y = m.b * pts[at * 3] + m.d * pts[at * 3 + 1] + m.f;
    const pressure = pts[at * 3 + 2];
    if (!state.last) {
      dab(ctx, kind, state, x, y, radius, strengthAt(pressure), stroke.color, look);
      state.last = [x, y];
      state.lastPressure = pressure;
      continue;
    }
    const dx = x - state.last[0];
    const dy = y - state.last[1];
    const length = Math.hypot(dx, dy);
    if (!length) continue;
    let along = step - state.owed;
    while (along <= length && made < MAX_DABS) {
      const share = along / length;
      dab(ctx, kind, state, state.last[0] + dx * share, state.last[1] + dy * share, radius, strengthAt(state.lastPressure + (pressure - state.lastPressure) * share), stroke.color, look);
      along += step;
      made++;
    }
    state.owed = Math.max(0, length - (along - step));
    state.last = [x, y];
    state.lastPressure = pressure;
  }
  state.next = total;
  ctx.restore();
}
